import { zodResolver } from "@hookform/resolvers/zod";
import { ActionIcon, Button } from "@mantine/core";
import { ContextModalProps } from "@mantine/modals";
import { WarningIcon } from "@phosphor-icons/react";
import { useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import z from "zod";

import { useSession } from "@/hooks/@custom/useSession";
import { MESSAGE } from "@/lib/zod/messages";
import { useGetDriverQueueLeaveStatusQuery, useGetQueueDriverPositionQuery, useSaveQueueDeleteMutation } from "@/store/apis/nextcargo";

import { Input } from "../Input";

const generateToken = (digits: number = 4) => {
  const min = Math.pow(10, digits - 1);
  const max = Math.pow(10, digits) - 1;
  return Math.floor(min + Math.random() * (max - min + 1)).toString();
};

type SaveQueueDeleteModalProps = {
  onQueueDeleted?: () => void;
};

export function SaveQueueDeleteModal({ id, context, innerProps }: ContextModalProps<SaveQueueDeleteModalProps>) {
  const { driver } = useSession();
  const driverId = driver?.id;

  const { data: queueDriverPosition, isLoading: isLoadingQueueDriverPosition } = useGetQueueDriverPositionQuery({ id: Number(driverId!) }, { skip: !driverId });
  const { data: driverQueueLeaveStatus, isLoading: isLoadingDriverQueueLeaveStatus } = useGetDriverQueueLeaveStatusQuery(
    { id: Number(driverId) },
    { skip: !driverId, pollingInterval: 1000 * 30 }
  );
  const [saveQueueDelete] = useSaveQueueDeleteMutation();

  type Schema = z.infer<typeof schema>;
  const generatedToken = useMemo(() => generateToken(), []);
  const schema = z
    .object({
      code: z
        .string({ required_error: MESSAGE.GLOBAL.REQUIRED })
        .nonempty({ message: MESSAGE.GLOBAL.NOT_EMPTY })
        .length(4, { message: MESSAGE.GLOBAL.LENGTH(4) }),
      queue: z.object({
        id: z.string().optional()
      })
    })
    .superRefine((val, ctx) => {
      if (val.code !== generatedToken) {
        ctx.addIssue({
          path: ["code"],
          code: z.ZodIssueCode.custom,
          message: "O código não confere."
        });
      }
    });

  const {
    formState: { errors, isSubmitting },
    handleSubmit,
    setFocus,
    control,
    reset
  } = useForm<Schema>({
    mode: "onSubmit",
    resolver: zodResolver(schema),
    disabled: isLoadingQueueDriverPosition || isLoadingDriverQueueLeaveStatus,
    defaultValues: {
      code: "",
      queue: {
        id: undefined
      }
    }
  });
  const isLoading = isSubmitting || isLoadingQueueDriverPosition || isLoadingDriverQueueLeaveStatus;

  const handleCloseModal = () => {
    context.closeModal(id);
    reset();
  };

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const { queue } = data;

    if (!queue.id) {
      context.closeModal(id);
      toast.error("Você não está na fila ou não pode sair da fila no momento!");
      return;
    }

    if (!driverQueueLeaveStatus?.canLeave) {
      context.closeModal(id);
      toast.error("Você não pode sair da fila no momento!");
      return;
    }

    const { error } = await saveQueueDelete({ id: +queue.id });
    if (!error) {
      innerProps?.onQueueDeleted?.();
      handleCloseModal();
      reset();
    }
  };

  useEffect(() => {
    if (!isLoadingQueueDriverPosition) {
      setFocus("code");
      reset((values) => ({
        ...values,
        queue: {
          id: queueDriverPosition?.queue?.id ? String(queueDriverPosition.queue.id) : undefined
        }
      }));
    }
  }, [isLoadingQueueDriverPosition]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <header className="flex flex-col items-center justify-center gap-2 text-red-500">
        <ActionIcon variant="light" color="red" radius="xl" size="xl">
          <WarningIcon weight="bold" size={24} />
        </ActionIcon>
        <h1 className="text-center text-xl font-semibold text-current">Para editar seu perfil, é necessário sair da fila atual. Deseja continuar?</h1>
      </header>

      <div className="flex flex-col items-center justify-center gap-[inherit]">
        <div className="flex flex-col gap-[inherit]">
          <p className="text-center text-gray-700">
            <strong className="text-xl">Estou ciente e desejo continuar</strong>
            <br />
            Digite o código <span className="text-primary font-bold">{generatedToken}</span> na caixa ao lado
          </p>

          <div className="flex flex-col items-center justify-center gap-1">
            <Input.Pin control={control} name="code" inputMode="numeric" />
            {errors.code && <p className="text-red-500">{errors.code.message}</p>}
          </div>
        </div>

        <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-2">
          <Button variant="outline" color="red" onClick={() => context.closeAll()} disabled={isLoading}>
            Cancelar
          </Button>

          <Button type="submit" color="red" loading={isLoading}>
            Confirmar
          </Button>
        </div>
      </div>
    </form>
  );
}
